/**
 * droid_view.ts — pure helpers the droid renderer and the datapad share (2026-09-24). No DOM, no three.
 */
import { DROID_CLASSES, droidClassOf, type DroidClass } from './droid_classes';

/**
 * THE MODEL FACING: every droid's front must face local +Z (the server's yaw is atan2(dx, dz) and the
 * renderer sets rotation.y = yaw). LOOKED AT (renders from ±Z and a 90° turn, 2026-09-24): the battle,
 * medic, humanoid and ship models face +Z; the supply walker faces +X; the mega tank's guns point −X.
 * rotation.y = φ turns local +X to (cos φ, 0, −sin φ), so +X → +Z needs −π/2 and −X → +Z needs +π/2.
 */
export const MODEL_YAW: Record<string, number> = {
  droid_battle: 0, droid_medic: 0, droid_humanoid: 0, droid_ship: 0,
  droid_supply: -Math.PI / 2, droid_mega: Math.PI / 2,
};

export function modelYawOf(kind: string): number { return MODEL_YAW[kind] ?? 0; }

/** The rendered size (longest edge) of a droid kind; the two older droids stay small orbs-with-models. */
export function renderSizeOf(kind: string): number {
  if (kind === 'droid_aegis' || kind === 'droid_ember') return 1.1;
  const c = droidClassOf(kind);
  return c ? DROID_CLASSES[c].sizeM : 1.1;
}

/** Height above the ground the model's base sits at (the older droids float at their old 1.15 m). */
export function baseLiftOf(kind: string, hasModel: boolean): number {
  if (!hasModel || kind === 'droid_aegis' || kind === 'droid_ember') return 1.15;
  const c = droidClassOf(kind);
  return c ? DROID_CLASSES[c].hoverM : 0;
}

export function classOfKind(kind: string): DroidClass { return droidClassOf(kind) ?? 'battle'; }

/** 12 345.6 → "12,346"; small values keep one decimal. */
export function credits(n: number): string {
  if (!Number.isFinite(n)) return '∞';
  if (Math.abs(n) >= 100) return Math.round(n).toLocaleString('en-US');
  return (Math.round(n * 10) / 10).toString();
}

/** Hours → "3 h 20 min" / "45 min" / "12 s" / "∞". */
export function duration(hours: number): string {
  if (!Number.isFinite(hours)) return 'forever';
  const s = Math.max(0, hours * 3600);
  if (s < 60) return `${Math.round(s)} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  const h = Math.floor(s / 3600), m = Math.round((s - h * 3600) / 60);
  if (h >= 48) return `${Math.round(h / 24)} days`;
  return m > 0 ? `${h} h ${m} min` : `${h} h`;
}

export interface SurveyPoint { x: number; z: number; c: number; s: string }

/**
 * The survey's heat grid: points snapped to cells (the survey walked a square lattice) → a row-major
 * grid of concentrations (-1 = not sampled), with the lattice's bounds. The report map draws it.
 */
export function surveyHeat(points: SurveyPoint[], step: number): { cols: number; rows: number; x0: number; z0: number; cells: number[] } {
  if (points.length === 0 || step <= 0) return { cols: 0, rows: 0, x0: 0, z0: 0, cells: [] };
  let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
  for (const p of points) { x0 = Math.min(x0, p.x); z0 = Math.min(z0, p.z); x1 = Math.max(x1, p.x); z1 = Math.max(z1, p.z); }
  const cols = Math.round((x1 - x0) / step) + 1, rows = Math.round((z1 - z0) / step) + 1;
  const cells = new Array<number>(cols * rows).fill(-1);
  for (const p of points) {
    const i = Math.round((p.x - x0) / step), j = Math.round((p.z - z0) / step);
    if (i >= 0 && j >= 0 && i < cols && j < rows) cells[j * cols + i] = Math.max(cells[j * cols + i], p.c);
  }
  return { cols, rows, x0, z0, cells };
}

/** The shop line for a priced cargo entry: unit price × qty. */
export function shopTotal(priceSov: bigint, qty: number): bigint {
  // THE FINITE LAW (2026-09-28): the shop's quantity box feeds this on every keystroke — '1e400' is Infinity and NaN is
  // NaN, and BigInt() of either threw inside the input handler.
  const q = Number.isFinite(qty) ? Math.max(0, Math.min(Number.MAX_SAFE_INTEGER, Math.floor(qty))) : 0;
  return priceSov * BigInt(q);
}

/** The 5 % bazaar tax the seller loses (floored, never on the buyer). */
export function sellerNet(total: bigint): bigint {
  return total - (total * 500n) / 10_000n;
}

/**
 * THE PURSE (the gaps wave, round 2, 2026-09-28): a droid's money is its owner's (`droid_purse`, row security to the
 * owner); the public `droid_vitals` row carries health only. A droid the server has not migrated yet still carries its
 * money on the vitals row — read the purse first, the vitals second.
 */
export interface DroidMoney { pool: bigint; bonus: bigint; spent: bigint; funded: bigint; sales: bigint }
type PurseLike = { poolMilli: bigint; bonusMilli: bigint; spentMilli: bigint; fundedTotal: bigint; salesSov: bigint };
export function moneyOf(purse: PurseLike | null | undefined, vitals: PurseLike | null | undefined): DroidMoney {
  const p = purse ?? vitals;
  return { pool: p?.poolMilli ?? 0n, bonus: p?.bonusMilli ?? 0n, spent: p?.spentMilli ?? 0n, funded: p?.fundedTotal ?? 0n, sales: p?.salesSov ?? 0n };
}

/** Where a droid IS: its `droid_pos` row (written by every step that moves it) first, else the unit row's snapshot. */
export function livePosOf(pos: { x: number; z: number; yaw: number } | null | undefined, unit: { posX: number; posZ: number; yaw: number }): { x: number; z: number; yaw: number } {
  return pos ? { x: pos.x, z: pos.z, yaw: pos.yaw } : { x: unit.posX, z: unit.posZ, yaw: unit.yaw };
}
